import type { ListResult } from 'pocketbase'
import { useEffect, useMemo, useState } from 'react'
import { pb } from '@/lib/pocketbase'

export type LocationAuthStatus =
    | 'always'
    | 'whenInUse'
    | 'denied'
    | 'notDetermined'
    | 'restricted'
    | 'unknown'

export interface LocationAuthSnapshot {
    userId: string
    status: LocationAuthStatus
    accuracyAuthorization: 'full' | 'reduced' | 'unknown' | null
    enabled: boolean
    source: 'startup' | 'foreground' | 'change'
    platform: 'ios' | 'android' | 'web'
    recordedAt: string
}

interface RawRecord {
    user: string
    status: LocationAuthStatus
    accuracy_authorization?: 'full' | 'reduced' | 'unknown' | ''
    enabled?: boolean
    source: 'startup' | 'foreground' | 'change'
    platform: 'ios' | 'android' | 'web'
    created: string
}

function pickLatestPerUser(records: RawRecord[]): Map<string, LocationAuthSnapshot> {
    const latest = new Map<string, LocationAuthSnapshot>()
    for (const r of records) {
        const existing = latest.get(r.user)
        if (existing && existing.recordedAt >= r.created) continue
        latest.set(r.user, {
            userId: r.user,
            status: r.status,
            accuracyAuthorization: r.accuracy_authorization
                ? (r.accuracy_authorization as 'full' | 'reduced' | 'unknown')
                : null,
            enabled: r.enabled ?? false,
            source: r.source,
            platform: r.platform,
            recordedAt: r.created,
        })
    }
    return latest
}

/**
 * Fetches the most recent location-authorization snapshot for each user in the
 * org. Uses a direct PB query (not a live subscription) because the data
 * changes infrequently and the monitor's existing 60s tick is plenty of
 * granularity for this status.
 */
export function useUsersLocationAuth(
    orgId: string | null | undefined,
    userIds: string[]
): Map<string, LocationAuthSnapshot> {
    const [snapshots, setSnapshots] = useState<Map<string, LocationAuthSnapshot>>(new Map())

    const userIdsKey = useMemo(() => [...userIds].sort().join(','), [userIds])

    useEffect(() => {
        if (!orgId || !userIdsKey) {
            setSnapshots(new Map())
            return
        }

        let cancelled = false
        const ids = userIdsKey.split(',')
        const userFilter = ids.map(id => `user="${id}"`).join('||')
        const filter = `org="${orgId}" && (${userFilter})`

        // Cast to bypass strict pocketbase-ts typing — the new collection
        // doesn't exist in pbSchema.ts until the migration is applied and
        // the schema is regenerated.
        // biome-ignore lint/suspicious/noExplicitAny: see comment above
        const collection = (pb.collection as any)('location_auth_events')

        const fetched = collection.getList(1, 200, {
            filter,
            sort: '-created',
            fields: 'user,status,accuracy_authorization,enabled,source,platform,created',
        }) as Promise<ListResult<RawRecord>>

        fetched
            .then(result => {
                if (cancelled) return
                setSnapshots(pickLatestPerUser(result.items))
            })
            .catch(() => {
                // Surface no snapshots on error; the indicator simply renders
                // as "unknown" which is the correct fallback.
            })

        return () => {
            cancelled = true
        }
    }, [orgId, userIdsKey])

    return snapshots
}
